// Angular Imports
import { Injectable } from '@angular/core'

export interface IImageCompressionOptions {
	/** Max length (px) of the longest side of the output image. */
	maxDimension: number
	/** JPEG quality (0–1) used when re-encoding. */
	quality: number
	/** Files at or below this size (bytes) are returned untouched. */
	minSizeBytes: number
}

export const DEFAULT_IMAGE_COMPRESSION_OPTIONS: IImageCompressionOptions = {
	maxDimension: 2048,
	quality: 0.85,
	minSizeBytes: 1.5 * 1024 * 1024,
}

/**
 * Compress before upload: Cloudflare returns 413 above 100 MB per request.
 * Falls back to the original file on any failure or when compression doesn't shrink it.
 */
@Injectable({
	providedIn: 'root',
})
export class ImageCompressionService {
	async compress(
		file: File,
		options: IImageCompressionOptions = DEFAULT_IMAGE_COMPRESSION_OPTIONS
	): Promise<File> {
		if (!file.type.startsWith('image/') || file.size <= options.minSizeBytes) {
			return file
		}

		try {
			const blob = await this.#resizeToJpeg(file, options)
			if (!blob || blob.size >= file.size) {
				return file
			}
			return new File([blob], this.#toJpegName(file.name), {
				type: 'image/jpeg',
				lastModified: file.lastModified,
			})
		} catch (error) {
			console.error('ImageCompressionService.compress', error)
			return file
		}
	}

	async #resizeToJpeg(file: File, options: IImageCompressionOptions): Promise<Blob | null> {
		// `from-image` applies EXIF orientation; re-encoding drops EXIF, so portrait photos would end up rotated.
		const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
		try {
			const scale = Math.min(1, options.maxDimension / Math.max(bitmap.width, bitmap.height))
			const width = Math.round(bitmap.width * scale)
			const height = Math.round(bitmap.height * scale)

			const canvas = document.createElement('canvas')
			canvas.width = width
			canvas.height = height
			const context = canvas.getContext('2d')
			if (!context) return null

			// White background: transparent PNG areas would turn black in JPEG.
			context.fillStyle = '#ffffff'
			context.fillRect(0, 0, width, height)
			context.drawImage(bitmap, 0, 0, width, height)

			return await new Promise<Blob | null>((resolve) =>
				canvas.toBlob(resolve, 'image/jpeg', options.quality)
			)
		} finally {
			bitmap.close()
		}
	}

	#toJpegName(name: string): string {
		const dotIndex = name.lastIndexOf('.')
		const baseName = dotIndex > 0 ? name.slice(0, dotIndex) : name
		return `${baseName}.jpg`
	}
}
